import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Truck, Anchor, MapPin, Train, Globe, CheckCircle, ChevronRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REGION_ICONS = { STATE: MapPin, PORT: Anchor, RAIL: Train };

const CarrierRegisterPage = () => {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ email: '', company_name: '', phone: '', mc_number: '' });
  const [carrierType, setCarrierType] = useState('');
  const [regionIds, setRegionIds] = useState([]);
  const [regions, setRegions] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    axios.get(`${API}/regions`).then(r => setRegions(r.data)).catch(() => {});
  }, []);

  const filteredRegions = regions.filter(r =>
    carrierType === 'OTR' ? r.region_type === 'STATE' : (r.region_type === 'PORT' || r.region_type === 'RAIL')
  );

  // Group by country so US and Canada are visually separated
  const groupedRegions = filteredRegions.reduce((acc, r) => {
    const key = (r.country || 'US').toUpperCase();
    if (!acc[key]) acc[key] = [];
    acc[key].push(r);
    return acc;
  }, {});
  Object.values(groupedRegions).forEach(arr => arr.sort((a, b) => a.name.localeCompare(b.name)));

  const toggleRegion = (id) => {
    setRegionIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!carrierType) { toast.error('Please select a carrier type'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/carriers/register`, {
        ...form,
        carrier_type: carrierType,
        region_ids: regionIds,
      });
      setSuccess(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Registration failed');
    } finally { setSubmitting(false); }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4">
        <div className="bg-white border border-zinc-200 p-10 text-center max-w-md">
          <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
          <h2 className="text-2xl font-black text-zinc-900 mb-2">You're Registered!</h2>
          <p className="text-zinc-600 mb-6">
            You'll receive load notifications matching your type and regions. Check your email for available loads.
          </p>
          <a href="/load-board" className="inline-block bg-red-600 hover:bg-red-700 text-white px-8 py-3 font-bold uppercase tracking-wide">
            View Load Board
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50 py-8 sm:py-16">
      <div className="max-w-lg mx-auto px-4">
        <div className="text-center mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-black text-zinc-900 uppercase mb-2">Carrier Registration</h1>
          <p className="text-sm sm:text-base text-zinc-600">Sign up to receive load notifications in your service area</p>
        </div>

        {/* Step indicator */}
        <div className="flex items-center justify-center gap-3 sm:gap-4 mb-6 sm:mb-8">
          {[1, 2, 3].map(s => (
            <div key={s} className="flex items-center gap-2">
              <div className={`w-8 h-8 flex items-center justify-center font-bold text-sm ${step >= s ? 'bg-red-600 text-white' : 'bg-zinc-200 text-zinc-500'}`}>{s}</div>
              {s < 3 && <ChevronRight className="w-4 h-4 text-zinc-400" />}
            </div>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="bg-white border border-zinc-200 p-5 sm:p-8 space-y-5 sm:space-y-6">

          {/* Step 1: Contact Info */}
          {step === 1 && (
            <>
              <h3 className="text-lg font-black text-zinc-900 uppercase">Contact Information</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1">Email *</label>
                  <input type="email" required value={form.email} onChange={e => setForm({...form, email: e.target.value})}
                    className="w-full px-4 py-3 border border-zinc-300 focus:border-zinc-900 outline-none" placeholder="dispatch@yourcompany.com" data-testid="register-email" />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1">Company Name</label>
                  <input type="text" value={form.company_name} onChange={e => setForm({...form, company_name: e.target.value})}
                    className="w-full px-4 py-3 border border-zinc-300 focus:border-zinc-900 outline-none" placeholder="Your Trucking Company" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1">Phone</label>
                    <input type="text" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})}
                      className="w-full px-4 py-3 border border-zinc-300 focus:border-zinc-900 outline-none" placeholder="(555) 555-1234" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1">MC Number</label>
                    <input type="text" value={form.mc_number} onChange={e => setForm({...form, mc_number: e.target.value})}
                      className="w-full px-4 py-3 border border-zinc-300 focus:border-zinc-900 outline-none" placeholder="MC-123456" />
                  </div>
                </div>
              </div>
              <button type="button" disabled={!form.email} onClick={() => setStep(2)}
                className="w-full bg-zinc-900 hover:bg-zinc-800 text-white py-3 font-bold uppercase tracking-wide disabled:opacity-50"
                data-testid="step1-next">
                Next: Choose Your Type
              </button>
            </>
          )}

          {/* Step 2: Carrier Type */}
          {step === 2 && (
            <>
              <h3 className="text-lg font-black text-zinc-900 uppercase">What Type of Carrier Are You?</h3>
              <div className="space-y-4">
                <button type="button" onClick={() => { setCarrierType('OTR'); setRegionIds([]); }}
                  className={`w-full flex items-center gap-4 p-5 border-2 transition-colors text-left ${carrierType === 'OTR' ? 'bg-blue-50 border-blue-600' : 'border-zinc-200 hover:border-zinc-400'}`}
                  data-testid="register-type-otr">
                  <Truck className={`w-8 h-8 ${carrierType === 'OTR' ? 'text-blue-600' : 'text-zinc-400'}`} />
                  <div>
                    <div className="font-black text-zinc-900 text-lg">OTR</div>
                    <div className="text-sm text-zinc-500">Long-haul interstate freight (FTL, LTL, Intermodal)</div>
                  </div>
                </button>
                <button type="button" onClick={() => { setCarrierType('DRAYAGE'); setRegionIds([]); }}
                  className={`w-full flex items-center gap-4 p-5 border-2 transition-colors text-left ${carrierType === 'DRAYAGE' ? 'bg-orange-50 border-orange-600' : 'border-zinc-200 hover:border-zinc-400'}`}
                  data-testid="register-type-drayage">
                  <Anchor className={`w-8 h-8 ${carrierType === 'DRAYAGE' ? 'text-orange-600' : 'text-zinc-400'}`} />
                  <div>
                    <div className="font-black text-zinc-900 text-lg">Drayage</div>
                    <div className="text-sm text-zinc-500">Port and rail container moves (short-haul)</div>
                  </div>
                </button>
              </div>
              <div className="flex flex-col-reverse sm:flex-row gap-3">
                <button type="button" onClick={() => setStep(1)} className="flex-1 border-2 border-zinc-300 text-zinc-700 py-3 font-bold uppercase tracking-wide">Back</button>
                <button type="button" disabled={!carrierType} onClick={() => setStep(3)}
                  className="flex-1 bg-zinc-900 hover:bg-zinc-800 text-white py-3 font-bold uppercase tracking-wide disabled:opacity-50"
                  data-testid="step2-next">
                  Next: Select Regions
                </button>
              </div>
            </>
          )}

          {/* Step 3: Regions */}
          {step === 3 && (
            <>
              <h3 className="text-lg font-black text-zinc-900 uppercase">
                {carrierType === 'OTR' ? 'Which States Do You Operate In?' : 'Which Ports / Rail Ramps?'}
              </h3>
              <p className="text-sm text-zinc-500 -mt-4">
                Select your active areas. Leave all unselected to receive <strong>all nationwide loads</strong>.
              </p>

              <div className="max-h-[55vh] sm:max-h-64 overflow-y-auto overscroll-contain -mx-1 px-1">
                {['US', 'CA'].map((country) => {
                  const rows = groupedRegions[country];
                  if (!rows || rows.length === 0) return null;
                  const activeCls = carrierType === 'OTR'
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-orange-600 text-white border-orange-600';
                  return (
                    <div key={country} className="mb-4 last:mb-0">
                      <div className="text-[10px] font-black uppercase tracking-[0.18em] text-zinc-500 mb-2 flex items-center gap-2">
                        <span aria-hidden>{country === 'CA' ? '🇨🇦' : '🇺🇸'}</span>
                        {country === 'CA' ? 'Canada' : 'United States'}
                        <span className="text-zinc-300">·</span>
                        <span className="text-zinc-400 tracking-normal">{rows.length}</span>
                      </div>
                      <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2">
                        {rows.map(r => {
                          const Icon = REGION_ICONS[r.region_type] || MapPin;
                          const active = regionIds.includes(r.id);
                          return (
                            <button
                              key={r.id}
                              type="button"
                              onClick={() => toggleRegion(r.id)}
                              data-testid={`region-chip-${r.id}`}
                              className={`flex items-center gap-1.5 px-3 py-2 text-sm font-bold border-2 transition-colors min-h-[40px] w-full sm:w-auto ${active ? activeCls : 'border-zinc-200 text-zinc-600 hover:border-zinc-400 bg-white'}`}
                            >
                              <Icon className="w-3.5 h-3.5 flex-shrink-0" />
                              <span className="truncate text-left">{r.name}</span>
                              {r.code && !active && <span className="ml-auto text-[10px] text-zinc-400">{r.code}</span>}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>

              {regionIds.length === 0 && (
                <div className="flex items-center gap-2 bg-green-50 border border-green-200 p-3 text-sm text-green-700 font-medium">
                  <Globe className="w-4 h-4" /> You'll receive all {carrierType} loads nationwide
                </div>
              )}
              {regionIds.length > 0 && (
                <div className="text-sm text-zinc-500">{regionIds.length} region(s) selected</div>
              )}

              <div className="flex flex-col-reverse sm:flex-row gap-3">
                <button type="button" onClick={() => setStep(2)} className="flex-1 border-2 border-zinc-300 text-zinc-700 py-3 font-bold uppercase tracking-wide">Back</button>
                <button type="submit" disabled={submitting}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white py-3 font-bold uppercase tracking-wide disabled:opacity-50"
                  data-testid="register-submit">
                  {submitting ? 'Registering...' : 'Register Now'}
                </button>
              </div>
            </>
          )}
        </form>
      </div>
    </div>
  );
};

export default CarrierRegisterPage;
